import React, { useState, useMemo, useEffect, useRef } from 'react';
import { isEqual } from 'lodash';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Button, Form, Typography } from 'antd';

import DF from 'DisplayFramework';
import { toCurrency } from 'utils/utils';
import DataGrid from 'components/DataGrid/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import {
  findPurchaseOrderListByAnyRef,
  PurchaseOrderListSearchResponse,
  FindPurchaseOrderListByAnyRefParams,
} from 'services/purchaseOrder';
import TasksListPopover from '../workflow/TasksListPopover';
import ParListSelect from 'components/ParListSelect';

type Values = {
  purchaseOrders: PurchaseOrderListSearchResponse[];
};
type POListProps =  {
  onSelect?: (item: PurchaseOrderListSearchResponse) => void;
}
const PurchaseOrderListModal = (props: POListProps) => {
  const [form] = Form.useForm();

  const [
    searchParams,
    setSearchParams,
  ] = useState<FindPurchaseOrderListByAnyRefParams>({
    clientname: '',
    clientreference: '',
    ctreference: '',
    poreference: '',
    postatus: '',
    propreference: '',
    refsupplier: '',
    suppliername: '',
  });

  const fieldName = 'purchaseOrders';
  const { onSelect } = props;

  const { data, refetch, isFetching } = useQuery(
    ['findPurchaseOrderListByAnyRef', searchParams],
    () =>
      findPurchaseOrderListByAnyRef(searchParams).then((res) => res.list || []),
    { refetchOnMount: false }
  );
  const firstUpdate = useRef(true);
  useEffect(() => {
    if (data) {
      form.setFieldsValue({ [fieldName]: data });
    }
    else
    {
      form.setFieldsValue({ [fieldName]: [] });
    }
  }, [form, data]);


  const columns = useMemo<DataGridColDef<PurchaseOrderListSearchResponse>[]>(
    () => [
      {
        dataIndex: 'poreference',
        filtrable: true,
        fixed: 'left',
        render: (value, record) => {
          if (onSelect) {
            return (
              <Typography.Link onClick={() => onSelect(record)}>
                {value}
              </Typography.Link>
            );
          } else
            return (
              <Link to={`/purchase-order/${record.poid}`}>{value}</Link>
            );
        }
      },
      { dataIndex: 'podate' },
      {
        dataIndex: 'postatus',
        filtrable: 'postatus',
        filterComponent:<ParListSelect parcode="POSTATUS" />,
        render: (_, record) => record.postatuslabel,
      },
      { dataIndex: 'poexpecteddeliverydate', width: 200 },
      { dataIndex: 'refsupplier', filtrable: true },
      { dataIndex: 'suppliername', filtrable: true },
      { dataIndex: 'ctreference', filtrable: true },
      { dataIndex: 'clientreference', filtrable: true },
      { dataIndex: 'clientname', filtrable: true },
      { dataIndex: 'ponetamount', fixed: 'right', render:(value, record) => toCurrency(value,record.currcode) },
      {
        comcode: 'actions',
        key: 'operation',
        fixed: 'right',
        width: 100,
        render: (text, record) => (
          <TasksListPopover
            entityId={record.poid}
            entityReference={record.poreference}
            title={
              record?.poreference + ' - ' + record?.suppliername
            }
            entityName="PURORDER"
            onClose={refetch}
          >
            <Button size="small">Actions</Button>
          </TasksListPopover>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refetch]
  );

  return (
    <DF.Provider pagcode="polist">
      <Form form={form}>
        <Form.Item
          noStyle
          shouldUpdate={(prevValue: Values, nextValue: Values) => {
            return !isEqual(prevValue, nextValue);
          }}
        >
          {({ getFieldValue }) => {
            const purchaseOrders: PurchaseOrderListSearchResponse[] = getFieldValue(
              fieldName
            );
            return (
              <DataGrid
                rowKey="poid"
                isLoading={isFetching}
                dataSource={purchaseOrders}
                columns={columns}
                onSearch={(values) => {
                  setSearchParams(values);
                  firstUpdate.current = false;
                  refetch();
                }}
                initialSearchValues={searchParams}
                titleComcode="purchaseOrder"
                hideExtraAction={!!onSelect}

              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

export default PurchaseOrderListModal;
